/**
 * Keyboard → bus layer, installed once per scene and removed on shutdown.
 *   Enter / Space / Z → input:confirm      Esc / X → input:cancel      Tab / M → input:menu
 *   Arrows / WASD     → input:dir (emitted on change; also pollable via dir())
 *   Shift / Ctrl held → input:fastText { held } (dialogue may also poll isFastText())
 *   Pointer tap (short press, little movement) → input:tapWorld { worldX, worldY } via the main camera.
 */
import Phaser from 'phaser';
import { bus, Events } from '@/core/events';

export interface InputMapHandle {
  /** Current keyboard direction vector (-1..1 per axis, not normalised). */
  dir(): { x: -1 | 0 | 1; y: -1 | 0 | 1 };
  isFastText(): boolean;
  destroy(): void;
}

/** Alias kept for early consumers; the typed event is `Events.INPUT_FAST_TEXT` ({ held: boolean }). */
export const INPUT_FAST_TEXT: typeof Events.INPUT_FAST_TEXT = Events.INPUT_FAST_TEXT;

const installed = new WeakMap<Phaser.Scene, InputMapHandle>();
const KC = Phaser.Input.Keyboard.KeyCodes;
type Key = Phaser.Input.Keyboard.Key;

export function getInputMap(scene: Phaser.Scene): InputMapHandle | null { return installed.get(scene) ?? null; }

export function installInputMap(scene: Phaser.Scene): InputMapHandle {
  const existing = installed.get(scene);
  if (existing) return existing;
  const kb = scene.input.keyboard;
  const keys: Record<string, Key> = kb
    ? (kb.addKeys({ up: KC.UP, down: KC.DOWN, left: KC.LEFT, right: KC.RIGHT, w: KC.W, a: KC.A, s: KC.S, d: KC.D, enter: KC.ENTER, space: KC.SPACE, z: KC.Z, esc: KC.ESC, x: KC.X, tab: KC.TAB, m: KC.M, shift: KC.SHIFT, ctrl: KC.CTRL }, false, false) as Record<string, Key>)
    : {};
  const CAPTURE = [KC.UP, KC.DOWN, KC.LEFT, KC.RIGHT, KC.SPACE, KC.TAB];
  kb?.addCapture(CAPTURE);

  const down = (k: Key | undefined) => !!k && k.isDown;
  const dir = (): { x: -1 | 0 | 1; y: -1 | 0 | 1 } => {
    const x = (down(keys['right']) || down(keys['d']) ? 1 : 0) - (down(keys['left']) || down(keys['a']) ? 1 : 0);
    const y = (down(keys['down']) || down(keys['s']) ? 1 : 0) - (down(keys['up']) || down(keys['w']) ? 1 : 0);
    return { x: x as -1 | 0 | 1, y: y as -1 | 0 | 1 };
  };
  const isFastText = () => down(keys['shift']) || down(keys['ctrl']);

  let lastDir = { x: 0, y: 0 };
  let lastFast = false;
  const emitDir = () => {
    const d = dir();
    if (d.x === lastDir.x && d.y === lastDir.y) return;
    lastDir = d;
    bus.emitTyped(Events.INPUT_DIR, { x: d.x, y: d.y, source: 'keyboard' });
  };
  const emitFast = () => {
    const f = isFastText();
    if (f === lastFast) return;
    lastFast = f;
    bus.emitTyped(Events.INPUT_FAST_TEXT, { held: f });
  };
  const listeners: { key: Key; ev: string; fn: (...a: unknown[]) => void }[] = [];
  const onKey = (name: string, ev: 'down' | 'up', fn: (key: Key, event: KeyboardEvent) => void) => {
    const k = keys[name];
    if (!k) return;
    const wrapped = ((key: Key, event: KeyboardEvent) => fn(key, event)) as (...a: unknown[]) => void;
    k.on(ev, wrapped);
    listeners.push({ key: k, ev, fn: wrapped });
  };
  const oncePressed = (name: string, emit: () => void) => onKey(name, 'down', (_k, e) => { if (!e.repeat) emit(); });
  oncePressed('enter', () => bus.emitTyped(Events.INPUT_CONFIRM, { source: 'keyboard' }));
  oncePressed('space', () => bus.emitTyped(Events.INPUT_CONFIRM, { source: 'keyboard' }));
  oncePressed('z', () => bus.emitTyped(Events.INPUT_CONFIRM, { source: 'keyboard' }));
  oncePressed('esc', () => bus.emitTyped(Events.INPUT_CANCEL, { source: 'keyboard' }));
  oncePressed('x', () => bus.emitTyped(Events.INPUT_CANCEL, { source: 'keyboard' }));
  oncePressed('tab', () => bus.emitTyped(Events.INPUT_MENU, { source: 'keyboard' }));
  oncePressed('m', () => bus.emitTyped(Events.INPUT_MENU, { source: 'keyboard' }));
  for (const n of ['up', 'down', 'left', 'right', 'w', 'a', 's', 'd']) { onKey(n, 'down', emitDir); onKey(n, 'up', emitDir); }
  for (const n of ['shift', 'ctrl']) { onKey(n, 'down', emitFast); onKey(n, 'up', emitFast); }

  // Window blur: Phaser resets key state without emitting 'up'; re-sync our derived state.
  const onBlur = () => { emitDir(); emitFast(); };
  scene.game.events.on(Phaser.Core.Events.BLUR, onBlur);

  // Pointer taps → world position through the main camera.
  const TAP_MAX_PX = 12, TAP_MAX_MS = 350;
  const onPointerUp = (pointer: Phaser.Input.Pointer) => {
    if (pointer.getDistance() > TAP_MAX_PX || pointer.getDuration() > TAP_MAX_MS) return;
    const cam = scene.cameras.main;
    if (!cam) return;
    const p = pointer.positionToCamera(cam) as Phaser.Math.Vector2;
    bus.emitTyped(Events.INPUT_TAP_WORLD, { worldX: Math.round(p.x), worldY: Math.round(p.y) });
  };
  scene.input.on(Phaser.Input.Events.POINTER_UP, onPointerUp);

  let alive = true;
  const handle: InputMapHandle = {
    dir,
    isFastText,
    destroy() {
      if (!alive) return;
      alive = false;
      for (const l of listeners) l.key.off(l.ev, l.fn);
      scene.game.events.off(Phaser.Core.Events.BLUR, onBlur);
      scene.input.off(Phaser.Input.Events.POINTER_UP, onPointerUp);
      if (kb) { for (const k of Object.values(keys)) kb.removeKey(k, true); kb.removeCapture(CAPTURE); }
      installed.delete(scene);
      scene.events.off(Phaser.Scenes.Events.SHUTDOWN, handle.destroy);
    },
  };
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, handle.destroy);
  installed.set(scene, handle);
  return handle;
}
